import { useEffect } from 'react';
import useSeo, { type SeoData } from '@/hooks/useSeo';

export default function ArtigoGuiaCompleto() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoData: SeoData = {
    title: 'CSSBuy Spreadsheet: Guia Completo para Iniciantes 2026',
    description: 'Tudo sobre CSSBuy Spreadsheet: o que é, vantagens, como usar passo a passo, tabela de exemplo com produtos, riscos e dicas de segurança para comprar da China em 2026.',
    keywords: 'CSSBuy Spreadsheet, guia CSSBuy, comprar da China, spreadsheet CSSBuy, importação China Brasil',
    canonicalPath: '/artigo/cssbuy-spreadsheet-guia-completo',
    ogType: 'article',
    ogImage: 'https://readdy.ai/api/search-image?query=Modern%20laptop%20showing%20colorful%20spreadsheet%20with%20product%20listings%20on%20a%20warm%20oak%20desk%2C%20soft%20morning%20light%20through%20sheer%20curtains%2C%20cozy%20home%20office%2C%20editorial%20photography%20warm%20earthy%20tones%2C%20shallow%20depth%20of%20field&width=1200&height=630&seq=artigo-guia-hero-seo&orientation=landscape',
    schemaJsonLd: {
      '@type': 'BlogPosting',
      headline: 'CSSBuy Spreadsheet: Guia Completo para Iniciantes 2026',
      description: 'Guia completo sobre CSSBuy Spreadsheet para iniciantes. Aprenda o que é, vantagens, como usar com passo a passo, tabela de exemplo, riscos e dicas de segurança.',
      datePublished: '2026-06-01',
      dateModified: '2026-06-17',
      author: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      publisher: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      inLanguage: 'pt-BR',
      isAccessibleForFree: true,
    },
  };
  useSeo(seoData);

  return (
    <main className="min-h-screen bg-background-50">
      <article className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <nav className="mb-8">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar para Home
          </a>
        </nav>

        <header className="mb-12">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold tracking-wide mb-6">
            GUIA COMPLETO
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            CSSBuy Spreadsheet: Guia Completo para Iniciantes 2026
          </h1>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl">
            Tudo o que você precisa saber para dominar o CSSBuy Spreadsheet e começar a
            importar produtos da China com segurança, organização e economia.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500">
            <span>Atualizado: Junho 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>Leitura: 8 min</span>
          </div>
        </header>

        <div className="w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden mb-14">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20laptop%20showing%20colorful%20spreadsheet%20with%20product%20listings%20on%20a%20warm%20oak%20desk%2C%20soft%20morning%20light%20through%20sheer%20curtains%2C%20cozy%20home%20office%2C%20editorial%20photography%20warm%20earthy%20tones%2C%20shallow%20depth%20of%20field&width=1200&height=630&seq=artigo-guia-hero-v5&orientation=landscape"
            alt="CSSBuy Spreadsheet Guia Completo"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="prose-custom">
          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
              O que é o CSSBuy Spreadsheet?
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-4">
              O <strong>CSSBuy Spreadsheet</strong> é uma ferramenta de organização
              desenvolvida para ajudar compradores internacionais a gerenciar suas
              aquisições de produtos chineses de forma eficiente. Trata-se de uma
              planilha personalizada onde você pode registrar todos os detalhes dos
              produtos que deseja comprar, comparar preços entre diferentes lojas,
              calcular custos de frete e acompanhar o status de cada pedido.
            </p>
            <p className="text-foreground-700 leading-relaxed mb-4">
              Diferente de simplesmente navegar pelas lojas e adicionar itens ao
              carrinho, o CSSBuy Spreadsheet permite uma abordagem estratégica para
              suas compras internacionais. Com ele, você consegue visualizar o custo
              total de uma remessa antes mesmo de finalizar o pedido, evitando
              surpresas desagradáveis com taxas e fretes inesperados.
            </p>
            <p className="text-foreground-700 leading-relaxed">
              A ferramenta se tornou especialmente popular entre compradores
              brasileiros que buscam produtos de qualidade da China, pois oferece
              uma camada extra de controle e transparência em todo o processo de
              importação.
            </p>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
              Principais Vantagens do CSSBuy Spreadsheet
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center mt-1">
                  <i className="ri-check-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-1">
                    Organização Completa
                  </h3>
                  <p className="text-foreground-600 leading-relaxed text-sm">
                    Mantenha todos os seus pedidos, produtos e lojas organizados em
                    um único lugar. Nunca mais perca o controle do que comprou ou
                    deixou de comprar.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-secondary-100 flex items-center justify-center mt-1">
                  <i className="ri-money-dollar-circle-line text-secondary-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-1">
                    Controle de Custos
                  </h3>
                  <p className="text-foreground-600 leading-relaxed text-sm">
                    Compare preços entre diferentes fornecedores e calcule o custo
                    total incluindo frete, taxas e comissões antes de finalizar
                    qualquer compra.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-accent-100 flex items-center justify-center mt-1">
                  <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-1">
                    Decisões Mais Inteligentes
                  </h3>
                  <p className="text-foreground-600 leading-relaxed text-sm">
                    Com todos os dados organizados, você toma decisões de compra
                    baseadas em informações reais, não em impulsos.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center mt-1">
                  <i className="ri-time-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-1">
                    Economia de Tempo
                  </h3>
                  <p className="text-foreground-600 leading-relaxed text-sm">
                    Evite ter que refazer pesquisas e comparações. O spreadsheet
                    guarda seu histórico de forma estruturada para consultas futuras.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
              Exemplo de CSSBuy Spreadsheet
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-5">
              Veja como organizar suas compras usando a planilha. Este é um exemplo
              prático de como os dados ficam estruturados:
            </p>

            <div className="overflow-x-auto rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary-500 text-background-50">
                    <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Produto</th>
                    <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Loja</th>
                    <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Preço</th>
                    <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Peso</th>
                    <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Qualidade</th>
                    <th className="text-left px-5 py-3 font-semibold whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Nike Dunk Low', 'Loja A', '$45', '1.2kg', '★★★★★', 'Comprado'],
                    ['Jordan 4 Retro', 'Loja B', '$60', '1.5kg', '★★★★☆', 'Análise'],
                    ['Yeezy Slide', 'Loja C', '$25', '0.8kg', '★★★★★', 'Comprado'],
                    ['Essentials Hoodie', 'Loja D', '$38', '1.8kg', '★★★★☆', 'Enviado'],
                    ['Carhartt Cargo', 'Loja E', '$42', '1.4kg', '★★★☆☆', 'Aguardando'],
                  ].map((row, i) => (
                    <tr
                      key={i}
                      className={`${
                        i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/60'
                      } border-b border-background-200/50 last:border-0`}
                    >
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={`px-5 py-3 whitespace-nowrap ${
                            j === 0 ? 'font-semibold text-foreground-900' : 'text-foreground-700'
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
              Como Começar a Usar
            </h2>

            <div className="space-y-4">
              {[
                {
                  step: '1',
                  title: 'Crie sua conta no CSSBuy',
                  desc: 'Acesse o site principal e faça seu cadastro. O processo é simples e gratuito.',
                },
                {
                  step: '2',
                  title: 'Copie os links dos produtos',
                  desc: 'Navegue pelas lojas parceiras, encontre os produtos desejados e copie os links.',
                },
                {
                  step: '3',
                  title: 'Insira os dados no spreadsheet',
                  desc: 'Cole os links na planilha e preencha as informações: nome, preço, peso e observações.',
                },
                {
                  step: '4',
                  title: 'Compare as opções',
                  desc: 'Analise os dados organizados e escolha as melhores opções de compra.',
                },
                {
                  step: '5',
                  title: 'Finalize o pedido',
                  desc: 'Com tudo planejado, faça seu pedido com confiança e acompanhe o status.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/60"
                >
                  <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-bold text-sm">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
              Riscos e Como Evitá-los
            </h2>

            <div className="bg-accent-50 border border-accent-200/50 rounded-2xl p-6 md:p-8">
              <div className="flex gap-3 mb-4">
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                  <i className="ri-error-warning-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900">
                  Atenção aos riscos comuns
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex gap-2 text-sm text-foreground-700">
                  <span className="text-accent-500 font-bold mt-0.5">•</span>
                  <span>
                    <strong>Taxas alfandegárias:</strong> Declare valores realistas para
                    evitar cobranças excessivas na alfândega brasileira.
                  </span>
                </li>
                <li className="flex gap-2 text-sm text-foreground-700">
                  <span className="text-accent-500 font-bold mt-0.5">•</span>
                  <span>
                    <strong>Atrasos no envio:</strong> Escolha métodos de frete
                    rastreados e tenha paciência — importações podem levar de 15 a 60
                    dias.
                  </span>
                </li>
                <li className="flex gap-2 text-sm text-foreground-700">
                  <span className="text-accent-500 font-bold mt-0.5">•</span>
                  <span>
                    <strong>Erros de tamanho:</strong> Verifique as tabelas de medidas
                    com atenção. Tamanhos chineses costumam ser menores que os
                    brasileiros.
                  </span>
                </li>
                <li className="flex gap-2 text-sm text-foreground-700">
                  <span className="text-accent-500 font-bold mt-0.5">•</span>
                  <span>
                    <strong>Lojas não confiáveis:</strong> Sempre verifique a reputação
                    do vendedor antes de comprar. Priorize lojas com boas avaliações.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
              Perguntas Frequentes
            </h2>

            <div className="space-y-4">
              {[
                {
                  q: 'Preciso pagar para usar o CSSBuy Spreadsheet?',
                  a: 'Não! O registro no CSSBuy é gratuito. Você só paga pelos produtos que comprar e pelo frete.',
                },
                {
                  q: 'Posso usar o spreadsheet no celular?',
                  a: 'Sim, a plataforma é responsiva e funciona bem tanto no computador quanto em dispositivos móveis.',
                },
                {
                  q: 'Quanto tempo os produtos demoram para chegar?',
                  a: 'Depende do método de envio. Linhas expressas entregam em 7-15 dias, linhas econômicas podem levar de 30 a 60 dias.',
                },
                {
                  q: 'É seguro comprar da China pelo CSSBuy?',
                  a: 'Sim, desde que você siga as boas práticas: verifique a reputação das lojas, declare valores corretos e use frete rastreado.',
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-foreground-900 font-semibold text-sm md:text-base list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              Pronto para Começar?
            </h2>
            <p className="text-background-50/85 mb-6 max-w-md mx-auto leading-relaxed">
              Acesse o CSSBuy Spreadsheet agora mesmo e comece a importar produtos da
              China com total controle e segurança.
            </p>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
            >
              CSSBuy Spreadsheet
              <i className="ri-arrow-right-up-line text-lg"></i>
            </a>
          </section>

          <section className="mt-12 pt-10 border-t border-background-200/70">
            <h3 className="text-lg font-semibold text-foreground-900 mb-4">
              Leia também
            </h3>
            <div className="flex flex-wrap gap-3">
              <a
                href="/artigo/como-usar-cssbuy-spreadsheet"
                className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
              >
                Como Usar CSSBuy Spreadsheet
              </a>
              <a
                href="/artigo/comparacao-cssbuy-pandabuy-superbuy"
                className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
              >
                Comparação de Agentes
              </a>
              <a
                href="/artigo/seguranca-cssbuy-spreadsheet"
                className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
              >
                Segurança nas Compras
              </a>
              <a
                href="/artigo/frete-china-brasil"
                className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
              >
                Frete China Brasil
              </a>
              <a
                href="/"
                className="px-4 py-2 bg-primary-100 hover:bg-primary-200 rounded-full text-sm text-primary-700 transition-colors font-medium"
              >
                ← Voltar para Home
              </a>
            </div>
          </section>
        </div>
      </article>
    </main>
  );
}